import { Routes, Route, useParams, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import Chevron from "./Chevron";

/**
 * Main App component that sets up routing for two progress tracker modes:
 * - QueryParamProgress: Progress tracker based on URL query parameters
 * - ApiProgress: Progress tracker based on API data (with pre/post admission split)
 */
export default function App() {
  return (
    <Routes>
      {/* Query parameter-based tracker */}
      <Route path="/" element={<QueryParamProgress />} />

      {/* API-based tracker */}
      <Route path="/api-progress/:studentStatusId" element={<ApiProgress />} />
    </Routes>
  );
}

/**
 * Progress tracker that reads its state from URL query parameters.
 * Used for quick demos or static progress visualization.
 */
function QueryParamProgress() {
  const [searchParams] = useSearchParams();
  const stagesParam = searchParams.get("stages");
  const activeStage = searchParams.get("activeStage");
  const successStage = searchParams.get("successStage");
  const failureStage = searchParams.get("failureStage");

  // Build steps from the comma-separated stages param
  let allSteps = stagesParam
    ? stagesParam.split(",").map((stage) => ({
        id: stage,
        label: stage
          .replace(/_/g, " ")
          .replace(/\b\w/g, (c) => c.toUpperCase()),
        formattedDate: "-",
      }))
    : [];

  // Remove conflicting stages based on current status
  if (activeStage === successStage) {
    allSteps = allSteps.filter((step) => step.id !== failureStage);
  } else if (activeStage === failureStage) {
    allSteps = allSteps.filter((step) => step.id !== successStage);
  } else {
    allSteps = allSteps.filter((step) => step.id !== failureStage);
  }

  // Calculate progress percent
  const currentIndex = allSteps.findIndex((step) => step.id === activeStage);
  const rawProgress =
    allSteps.length > 1
      ? currentIndex >= 0
        ? (currentIndex / (allSteps.length - 1)) * 100
        : 0
      : currentIndex >= 0
      ? 100
      : 0;

  const [progressPercent, setProgressPercent] = useState(0);

  // Animate progress bar fill
  useEffect(() => {
    const timeout = setTimeout(() => {
      setProgressPercent(rawProgress);
    }, 100);
    return () => clearTimeout(timeout);
  }, [rawProgress]);

  return (
    <ProgressTracker
      allSteps={allSteps}
      progressPercent={progressPercent}
      activeStage={activeStage}
      successStage={successStage}
      failureStage={failureStage}
    />
  );
}

/**
 * Progress tracker that fetches student status from an API,
 * splits stages into pre- and post-admission, and handles failure logic.
 */
function ApiProgress() {
  const { studentStatusId } = useParams();
  const [stages, setStages] = useState([]);
  const [activeStage, setActiveStage] = useState(null);
  const [successStage, setSuccessStage] = useState(null);
  const [failureStage, setFailureStage] = useState(null);
  const [progressPercent, setProgressPercent] = useState(0);

  // Use mock data if ?mock=true is present in the URL
  const useMock =
    new URLSearchParams(window.location.search).get("mock") === "true";

  useEffect(() => {
    /**
     * Fetches student status data from the API or uses mock data.
     * Processes the payload to build the progress steps.
     */
    const fetchStages = async () => {
      let payload;

      if (useMock) {
        // Example mock data for demo/testing
        payload = {
          "application missing material date": null,
          "application review start date": 1753233600000,
          "application submitted date": 1753173600000,
          "i-20 issued date": 1753320000000,
          "proof of funds submitted date": 1753286400000,
          "sevis fee paid date": null,
          "student admitted date": 1753260000000,
          "student created date": 1753100820000,
          "student deferred date": null,
          "student enrolled date": null,
          "student rejected date": 1753341600000,
          "student withdrawn date": null,
          "visa appointment date": null,
          "visa appointment scheduled date": null,
          "visa approved date": null,
        };
      } else {
        try {
          const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/api/1.1/wf/student-status?student_status_id=${studentStatusId}`;
          const response = await fetch(apiUrl);
          const data = await response.json();
          payload = data?.response?.response || {};
        } catch (error) {
          console.error("Error fetching stages:", error);
          return;
        }
      }

      console.log("Endpoint payload:", payload);
      processPayload(payload);
    };

    /**
     * Processes the API or mock payload to build the ordered steps array,
     * handles insertion of failure stages, and determines the active stage.
     */
    const processPayload = (payload) => {
      const PRE_ADMISSION = [
        "student created date",
        "application submitted date",
        "application review start date",
        "student admitted date",
      ];
      const POST_ADMISSION = [
        "student admitted date",
        "proof of funds submitted date",
        "i-20 issued date",
        "sevis fee paid date",
        "visa approved date",
        "student enrolled date",
      ];
      const FAILURE = [
        "student deferred date",
        "student rejected date",
        "student withdrawn date",
        "application missing material date",
      ];
      const STAGE_LABELS = {
        "student created date": "Created",
        "application submitted date": "Submitted",
        "application review start date": "Review",
        "student admitted date": "Admitted",
        "proof of funds submitted date": "Proof of Funds",
        "i-20 issued date": "I-20 Issued",
        "sevis fee paid date": "SEVIS Fee Paid",
        "visa approved date": "Visa Approved",
        "student enrolled date": "Enrolled",
        "student deferred date": "Deferred",
        "student rejected date": "Rejected",
        "student withdrawn date": "Withdrawn",
        "application missing material date": "Missing Documents",
      };

      const SUCCESS_STAGE_FIELDS = ["student enrolled date"];

      // Determine if admitted
      const isAdmitted = Boolean(payload["student admitted date"]);

      // Choose base stages based on admission status
      let baseStages = isAdmitted ? [...POST_ADMISSION] : [...PRE_ADMISSION];

      // Filter failure stages that have a timestamp
      const failureStagesWithTimestamp = filterFailureStages(payload, FAILURE);

      // Insert failure stage at the correct position
      const activeFailureStage = failureStagesWithTimestamp.find(
        (f) => payload[f] !== null && payload[f] !== undefined
      );
      if (activeFailureStage) {
        // Remove if already present to avoid duplicates
        baseStages = baseStages.filter((f) => f !== activeFailureStage);

        const terminalFailures = [
          "student deferred date",
          "student withdrawn date",
          "student rejected date",
        ];

        if (terminalFailures.includes(activeFailureStage)) {
          // Insert as the last stage
          baseStages.push(activeFailureStage);
        } else {
          // Insert after last completed stage
          const lastCompletedIdx = baseStages
            .map((field) => payload[field])
            .reduce((acc, ts, idx) => (ts ? idx : acc), -1);
          baseStages.splice(lastCompletedIdx + 1, 0, activeFailureStage);
        }
      }

      // Map payload to steps with metadata
      let steps = baseStages.map((field) => {
        const rawTimestamp = payload[field] ? Number(payload[field]) : null;
        return {
          field,
          id: field.replace(/\s+/g, "_"),
          label: STAGE_LABELS[field] || field,
          rawTimestamp,
          formattedDate: rawTimestamp
            ? new Date(rawTimestamp).toISOString().split("T")[0]
            : null,
          isComplete: !!rawTimestamp,
        };
      });

      // Calculate days between steps for warning indicators
      const today = Date.now();
      steps = steps.map((step, idx) => {
        const nextTimestamp = steps[idx + 1]?.rawTimestamp;
        const daysBetween = step.rawTimestamp
          ? Math.floor(
              ((nextTimestamp || today) - step.rawTimestamp) /
                (1000 * 60 * 60 * 24)
            )
          : 0;
        return { ...step, daysBetween };
      });

      // Determine which stage is currently active, and set success/failure
      let activeStageId = null;
      let successStageId = null;
      let failureStageId = null;

      const terminalFailures = [
        "student deferred date",
        "student withdrawn date",
        "student rejected date",
      ];

      if (activeFailureStage && terminalFailures.includes(activeFailureStage)) {
        // If terminal failure, make it the active stage
        activeStageId = activeFailureStage.replace(/\s+/g, "_");
      } else {
        let currentIndex = steps.findIndex((s) => s.rawTimestamp === null);
        currentIndex =
          currentIndex === -1 ? steps.length - 1 : currentIndex - 1;
        activeStageId = steps[currentIndex]?.id || null;

        const finalStage = steps[currentIndex] || {};
        if (SUCCESS_STAGE_FIELDS.includes(finalStage.field)) {
          successStageId = finalStage.id;
        }
      }

      // Set failureStageId if a failure is present
      if (activeFailureStage) {
        failureStageId = activeFailureStage.replace(/\s+/g, "_");
      }

      setStages(steps);
      setActiveStage(activeStageId);
      setSuccessStage(successStageId);
      setFailureStage(failureStageId);
    };

    fetchStages();
  }, [studentStatusId, useMock]);

  // Calculate progress percent for the progress bar
  const currentIndex = stages.findIndex((s) => s.id === activeStage);
  const rawProgress =
    stages.length > 1
      ? currentIndex >= 0
        ? (currentIndex / (stages.length - 1)) * 100
        : 0
      : currentIndex >= 0
      ? 100
      : 0;

  // Animate progress bar fill
  useEffect(() => {
    const timeout = setTimeout(() => setProgressPercent(rawProgress), 100);
    return () => clearTimeout(timeout);
  }, [rawProgress]);

  return (
    <ProgressTracker
      allSteps={stages}
      progressPercent={progressPercent}
      activeStage={activeStage}
      successStage={successStage}
      failureStage={failureStage}
    />
  );
}

/**
 * Reusable progress tracker component that renders the chevron steps,
 * progress bar, and current status label.
 */
function ProgressTracker({
  allSteps,
  progressPercent,
  activeStage,
  successStage,
  failureStage,
}) {
  const WARNING_THRESHOLD_MIN = 7;
  const WARNING_THRESHOLD_MAX = 14;

  /**
   * Returns props for each chevron step, including color and status.
   */
  const getChevronProps = (step, index) => {
    const isSuccess = step.id === successStage;
    const isFailure = step.id === failureStage;
    const isCurrentStage = step.id === activeStage;
    const hasTimestamp = Boolean(
      step.formattedDate && step.formattedDate !== "-"
    );
    const isFirstStep = index === 0;
    const isFinalStep = index === allSteps.length - 1;

    let bgColor;

    // If a failure stage is active, grey out any step without a timestamp
    if (activeStage === failureStage && !hasTimestamp) {
      bgColor = "bg-gray-300";
    } else if (isSuccess) {
      bgColor = "bg-green-500";
    } else if (isFailure) {
      bgColor = "bg-red-500";
    } else if (isCurrentStage && hasTimestamp) {
      // Show warning colors for current stage if needed
      if (
        step.daysBetween >= WARNING_THRESHOLD_MIN &&
        step.daysBetween <= WARNING_THRESHOLD_MAX
      ) {
        bgColor = "bg-yellow-500";
      } else if (step.daysBetween > WARNING_THRESHOLD_MAX) {
        bgColor = "bg-orange-500";
      } else {
        bgColor = "bg-blue-600";
      }
    } else if (hasTimestamp) {
      bgColor = "bg-blue-600";
    } else {
      bgColor = "bg-zinc-300";
    }

    const textColor =
      hasTimestamp || isSuccess || isFailure ? "text-white" : "text-black";

    return {
      step,
      bgColor,
      textColor,
      isCurrentStage,
      isSuccess,
      isFailure,
      isFirstStep,
      isFinalStep,
      index,
      glowClass: "",
    };
  };

  const activeStep = allSteps.find((s) => s.id === activeStage);

  // Determine progress bar color based on current status
  let progressBarColor = "bg-blue-600";
  if (activeStep) {
    if (activeStage === successStage) {
      progressBarColor = "bg-green-500";
    } else if (activeStage === failureStage) {
      progressBarColor = "bg-red-500";
    } else if (
      activeStep.daysBetween >= WARNING_THRESHOLD_MIN &&
      activeStep.daysBetween <= WARNING_THRESHOLD_MAX
    ) {
      progressBarColor = "bg-yellow-500";
    } else if (activeStep.daysBetween > WARNING_THRESHOLD_MAX) {
      progressBarColor = "bg-orange-500";
    }
  }

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
      <div className="bg-white rounded-xl p-8 w-full">
        {/* Chevron Blocks */}
        <div className="flex w-full overflow-visible">
          {allSteps.map((step, index) => (
            <Chevron
              key={step.id}
              {...getChevronProps(step, index)}
              index={index}
            />
          ))}
        </div>

        {/* Progress Fill Indicator */}
        <div className="w-full mt-6 h-4 bg-gray-300 rounded-full overflow-hidden">
          <div
            className={`h-4 transition-all duration-700 rounded-full ${progressBarColor}`}
            style={{ width: `${progressPercent}%` }}
          ></div>
        </div>

        {/* Current Status */}
        <p className="mt-6 text-center text-gray-600">
          Current status:{" "}
          <strong>
            {allSteps.find((s) => s.id === activeStage)?.label ||
              "Status Unknown"}
          </strong>
        </p>
      </div>
    </div>
  );
}

/**
 * Helper to filter failure stages that have a timestamp.
 */
const filterFailureStages = (payload, failureStages) =>
  failureStages.filter((f) => payload[f] !== null && payload[f] !== undefined);
